import { WidgetModuleTransition, Article, WidgetModuleType } from '@wix/answers-api';
import {EventType} from '../common/logger/event-type';

export enum NavigationState {
	kb,
	tickets,
	search,
	category,
	article,
	articleUri,
	helped,
	chat
}

const navigationState = {
	kb: {
		home: '/kb',
		search: '/search',
		category: '/category',
		article: '/article',
		articleUri: '/article-uri',
	},
	tickets: {
		contact: '/tickets',
		helped: '/helped'
	},
	chat: '/chat'
};

type NavigationStateToUrlMap = {[key: number]: (...args: any[]) => string};

const navigationStateToUrlMap: NavigationStateToUrlMap = {
	[NavigationState.kb]: () => navigationState.kb.home,
	[NavigationState.tickets]: () => navigationState.tickets.contact,
	[NavigationState.helped]: () => navigationState.tickets.helped,
	[NavigationState.search]: (searchTerm: string) => navigationState.kb.search + '/' + searchTerm,
	[NavigationState.category]: (categoryId: string) => navigationState.kb.category + '/' + categoryId,
	[NavigationState.article]: (articleId: string) => navigationState.kb.article + '/' + articleId,
	[NavigationState.articleUri]: (articleUri: string) => navigationState.kb.articleUri + '/' + articleUri,
	[NavigationState.chat]: () => navigationState.chat
};

const getModuleFromState = (state: NavigationState): WidgetModuleType => {
	if (state === NavigationState.tickets || state === NavigationState.helped) {
		return WidgetModuleType.TICKET;
	} else {
		return WidgetModuleType.KB;
	}
};

export const getNavigationUrl = (state: NavigationState, data: any): string => {
	return navigationStateToUrlMap[state](data);
};

export type SearchResultData = {
	articles: Article[],
	searchTerm: string
};

export type StateData = {
	state: NavigationState,
	data: any
};

type LogEventFn = (event: EventType, data: any) => void;

export type WidgetNavigator = {
	isKbModuleHasBack: () => boolean;
	getModuleTransitionName: (transition: WidgetModuleTransition) => string;
	getKbTransitionName: () => string;
	onSearchCompleted: (articles: Article[], searchTerm: string, logEvent: LogEventFn) => void;
	setSearchFocus: (isFocused: boolean, logEventFunc: LogEventFn) => void;
	isKbHomepage: () => boolean;
	goToState: (state: NavigationState, data: any, isBackHierarchy?: boolean) => void;
	getCurrModules: () => WidgetModuleType;
	setDefaultModule: (module: WidgetModuleType) => void;
	switchModules: (module: WidgetModuleType, goToStateCallBack?: any) => void;
	goBack: () => void;
	goBackBeforeSearch: () => void;
	setOverwriteTransition: (transition: WidgetModuleTransition) => void;
	getQueryParam: (name: string) => string | null;
	maybeAddInitialStateToKbHistory: (state: NavigationState, data: any) => void;
};

export const createWidgetNavigationHandler = (history: any): WidgetNavigator => {
	// let isHistoryInit: boolean = false;
	// let historyCount: number = 0;
	let lastNonSearchHistoryCount: number = 1;
	let currModule: WidgetModuleType = WidgetModuleType.KB;
	let isBack = false;
	let isBackHierarchy = false;
	let isSearch = false;
	let searchResultData: SearchResultData | null = null;
	let hideBack = false;
	let overwriteTransition: WidgetModuleTransition | 0 | null = null;
	const kbHistoryList: string[] = [];
	const ticketsHistoryList: string[] = [];

	// history.listen((location: any) => {
	// 	if (!isHistoryInit) {
	// 		kbHistoryList.push(location.pathname);
	// 		lastNonSearchHistoryCount = 1;
	// 		isHistoryInit = true;
	// 	}
	// });

	const isBackInHierarchy = (): boolean => {
		let backInHierarchy = false;
		if (kbHistoryList.length > 0) {
			const currHistory = kbHistoryList[kbHistoryList.length - 1];
			const prevHistory = kbHistoryList.length > 1 ? kbHistoryList[kbHistoryList.length - 2] : null;
			if (isBack ||
				currHistory.indexOf(navigationState.kb.home) === 0
				|| (isBackHierarchy) &&
					currHistory.indexOf(navigationState.kb.category) === 0 &&
					prevHistory && prevHistory.indexOf(navigationState.kb.category) === 0
				|| currHistory.indexOf(navigationState.kb.category) === 0 &&
					prevHistory && prevHistory.indexOf(navigationState.kb.article) === 0
			) {
				backInHierarchy = true;
			}
		}
		return backInHierarchy;
	};

	const goToState = (state: NavigationState, data: any) => {
		const url = getNavigationUrl(state, data);
		if (currModule === WidgetModuleType.KB) {
			kbHistoryList.push(url);
			if (state === NavigationState.search) {
				isSearch = true;
			} else {
				lastNonSearchHistoryCount = kbHistoryList.length;
				isSearch = false;
			}
		} else if (currModule === WidgetModuleType.TICKET) {
			ticketsHistoryList.push(url);
		}
		history.replace(url);
	};

	const switchModules = (module: WidgetModuleType, state: NavigationState | null = null, data: any = null) => {
		currModule = module;
		if (module === WidgetModuleType.KB) {
			if (state !== null) {
				hideBack = true;
				goToState(state, data);
			} else if (kbHistoryList.length) {
				const lastIndex: number = kbHistoryList.length - 1;
				history.replace(kbHistoryList[lastIndex]);
			} else {
				goToState(NavigationState.kb, {});
			}
		} else if (currModule === WidgetModuleType.TICKET) {
			if (state !== null) {
				goToState(state, data);
			} else if (ticketsHistoryList.length) {
				const lastIndex: number = ticketsHistoryList.length - 1;
				history.replace(ticketsHistoryList[lastIndex]);
			} else {
				goToState(NavigationState.tickets, {});
			}
		} else if (module === WidgetModuleType.CHAT) {
			goToState(NavigationState.chat, {});
		}
	};

	const isKbHomepage = (): boolean => {
		const lastIndex: number = kbHistoryList.length - 1;
		const kbHompageUrl = getNavigationUrl(NavigationState.kb, {});
		return (kbHistoryList[lastIndex] || '').startsWith(kbHompageUrl);
	};

	return {
		isKbModuleHasBack: () => {
			const showBack = !isKbHomepage() && !hideBack && kbHistoryList.length > 1;
			hideBack = false;
			return showBack;
		},
		getCurrModules: () => {
			return currModule;
		},
		getModuleTransitionName: (transition: WidgetModuleTransition) => {
			transition = overwriteTransition || transition;
			// if (transition === WidgetModuleTransition.FLIP) {
			// 	return 'flip-page';
			// } else {
			return currModule === WidgetModuleType.KB ? 'slide-down-page' : 'slide-up-page';
			// }
		},
		getKbTransitionName: () => {
			return isSearch ? 'fade-page' : (isBackInHierarchy() ? 'slide-back-page' : 'slide-page');
		},
		isKbHomepage,
		setDefaultModule: (defaultModule: WidgetModuleType) => {
			currModule = defaultModule;
			const state: NavigationState = defaultModule === WidgetModuleType.TICKET ?
				NavigationState.tickets : defaultModule === WidgetModuleType.CHAT ? NavigationState.chat : NavigationState.kb;
			const url = getNavigationUrl(state, {}) + location.search;
			if (currModule === WidgetModuleType.KB) {
				kbHistoryList.push(url);
				history.replace(url);
			} else if (currModule === WidgetModuleType.TICKET) {
				ticketsHistoryList.push(url);
				history.replace(url);
			} else if (currModule === WidgetModuleType.CHAT) {
				ticketsHistoryList.push(url);
				history.replace(url);
			}
		},
		switchModules: (module: WidgetModuleType, goToStateCallBack: any = null) => {
			switchModules(module, goToStateCallBack);
		},
		goToState: (state: NavigationState, data: any, backInHierarchy: boolean = false) => {
			const stateModule = getModuleFromState(state);
			if (stateModule !== currModule) {
				return switchModules(stateModule, state, data);
			}
			isBack = false;
			isBackHierarchy = backInHierarchy;
			goToState(state, data);
		},
		goBack: () => {
			kbHistoryList.pop();
			isBack = true;
			isBackHierarchy = false;
			const lastIndex: number = kbHistoryList.length - 1;
			history.replace(kbHistoryList[lastIndex]);
		},
		goBackBeforeSearch: () => {
			const nonHistoryDiff = lastNonSearchHistoryCount - kbHistoryList.length;
			kbHistoryList.splice(nonHistoryDiff);
			lastNonSearchHistoryCount = kbHistoryList.length;
			const lastIndex: number = kbHistoryList.length - 1;
			if (lastIndex >= 0) {
				history.replace(kbHistoryList[lastIndex]);
			}
		},
		setOverwriteTransition: (transition: WidgetModuleTransition) => {
			overwriteTransition = transition;
		},
		setSearchFocus: () => {
			// do nothing, bi moved to onSearchCompleted
		},
		// tslint:disable-next-line:max-line-length
		onSearchCompleted: (articles: Article[], searchTerm: string, logEventFunc) => {
			searchResultData = {articles, searchTerm};

			if (searchResultData.articles.length === 0) {
				logEventFunc(EventType.SEARCH_NO_RESULT, {term: searchResultData.searchTerm});
			} else {
				const firstAnswer = searchResultData.articles[0];
				logEventFunc(
					EventType.SEARCH,
					{term: searchResultData.searchTerm, firstAnswer: firstAnswer.title, firstResultId: firstAnswer.id}
				);
			}
		},
		getQueryParam: (name: string) => {
			const match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
			return match && decodeURIComponent(match[1].replace(/\+/g, ' ')) || null;
		},
		maybeAddInitialStateToKbHistory: (state, data) => {
			if (!kbHistoryList.length) {
				const url = getNavigationUrl(state, data);
				kbHistoryList.push(url);
			}
		}
	};
};
